import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, ZoomControl } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

import { PositionMsgType } from "./types";
import { InstallBaseLayers } from "./BaseLayers";
import { RemoveDefaultZoom, ChooseViewControl } from "./MapChrome";
import { AutoFitBounds } from "./AutoFitBounds";
import { MarkersLayer } from "./MarkersLayer";
import { useTranslation } from "react-i18next";
import { useHereTileUrls } from "./hooks/useHereUrls";
import {MapFabs} from "./MapFabs";
import {MapSettings} from "./MapSettings";
import "./hereMapsStyle.scss";

type Props = { PositionList: PositionMsgType[]; apiKey?: string };

/** Measures header and syncs CSS var (no inline styles needed) */
function useHeaderHeightVar(selector = ".App", fallback = 64, cssVar = "--header-h") {
  const [h, setH] = useState<number>(fallback);

  useEffect(() => {
    let el: HTMLElement | null = null;
    let ro: ResizeObserver | null = null;

    const read = () => Math.round(el ? el.getBoundingClientRect().height : fallback);
    const apply = (px: number) => {
      document.documentElement.style.setProperty(cssVar, `${px}px`);
      setH(px);
    };

    const attach = () => {
      el = document.querySelector<HTMLElement>(selector);
      apply(read());
      if (el && "ResizeObserver" in window) {
        ro?.disconnect();
        ro = new ResizeObserver(() => apply(read()));
        ro.observe(el);
      }
    };

    attach();
    const onResize = () => apply(read());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    const tid = window.setTimeout(attach, 150); // late mount safety

    return () => {
      window.clearTimeout(tid);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      ro?.disconnect();
    };
  }, [selector, fallback, cssVar]);

  return h;
}

export const CommunicationHistoryMap: React.FC<Props> = ({ PositionList, apiKey }) => {
  const { t } = useTranslation();
  const urls = useHereTileUrls({ apiKey, lang: "en", ppi: 160 });

  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Control.Layers | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const gearBtnRef = useRef<HTMLButtonElement | null>(null);

  const [delayMode, setDelayMode] = useState<1 | 2>(1);
  const [showLow, setShowLow] = useState(true);
  const [showMedium, setShowMedium] = useState(true);
  const [showHigh, setShowHigh] = useState(true);
  const [controlsOpen, setControlsOpen] = useState(false);

  // CSS-based fullscreen (under dynamic header)
  useHeaderHeightVar(".App", 64, "--header-h");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // filter
  const filtered = useMemo(() => {
    return (PositionList || []).filter((p) => {
      if (!(p.gpsLatitude && p.gpsLongitude)) return false;
      const gps = +new Date(p.gpsTimestamp);
      const msg = +new Date(p.messageTimestamp);
      const saved = p.timeSaved ? +new Date(p.timeSaved) : msg;
      const d = delayMode === 1 ? (msg - gps) / 1000 : (saved - msg) / 1000;
      if (d < 60) return showLow;
      if (d < 1800) return showMedium;
      return showHigh;
    });
  }, [PositionList, delayMode, showLow, showMedium, showHigh]);

  // prevent map from reacting to clicks/wheel on the panel & gear
  useEffect(() => {
    const p = panelRef.current;
    const g = gearBtnRef.current;
    if (p) {
      L.DomEvent.disableClickPropagation(p);
      L.DomEvent.disableScrollPropagation(p);
    }
    if (g) {
      L.DomEvent.disableClickPropagation(g);
      L.DomEvent.on(g, "wheel", L.DomEvent.stop);
    }
    return () => {
      if (g) L.DomEvent.off(g, "wheel", L.DomEvent.stop);
    };
  }, []);

  // Recalculate map size when layout changes
  useEffect(() => { mapRef.current?.invalidateSize(); }, [controlsOpen, isFullscreen]);
  useEffect(() => { setTimeout(() => mapRef.current?.invalidateSize(), 60); }, [isFullscreen]);

  // Lock body scroll while in CSS fullscreen
  useEffect(() => {
    document.body.classList.toggle("no-scroll", isFullscreen);
    return () => document.body.classList.remove("no-scroll");
  }, [isFullscreen]);

  // Toggle CSS fullscreen (no Fullscreen API so header stays visible)
  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  const toggleViews = () => {
    const ctrl = layersRef.current;
    if (!ctrl) return;
    const el = (ctrl as any).getContainer?.();
    const isOpen = !!el && el.classList.contains("leaflet-control-layers-expanded");
    isOpen ? ctrl.collapse() : ctrl.expand();
  };

  return (
    <div className={`map-frame ${isFullscreen ? "map-fullscreen" : ""}`}>
      <div className="map-main">
        {/* FABS */}
        <MapFabs
          ref={gearBtnRef}
          settingsOpen={controlsOpen}
          onToggleSettings={() => setControlsOpen(v => !v)}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
        />

        {/* SETTINGS PANEL */}
        <MapSettings
          ref={panelRef}
          open={controlsOpen}
          onClose={() => setControlsOpen(false)}
          title={t("TS_core:CalculateDelayFor") || "Calculate delay for"}
          delayMode={delayMode}
          setDelayMode={setDelayMode}
          showLow={showLow}
          setShowLow={setShowLow}
          showMedium={showMedium}
          setShowMedium={setShowMedium}
          showHigh={showHigh}
          setShowHigh={setShowHigh}
          labels={{
            onboard: t("TS_core:Onboard") || "Onboard",
            offboard: t("TS_core:Offboard") || "Offboard",
            posToMsg: t("TS_core:PositionTimeToMessageTime") || "Position time → Message time",
            msgToSaved: t("TS_core:MessageTimeToTimeSaved") || "Message time → Time saved",
            displayLevels: t("TS_core:DisplayDelayLevels") || "Display delay levels",
            low: t("TS_core:Low") || "Low",
            medium: t("TS_core:Medium") || "Medium",
            high: t("TS_core:High") || "High",
          }}
        />

        {/* MAP */}
        <MapContainer
          ref={mapRef}
          whenReady={() => requestAnimationFrame(() => mapRef.current?.invalidateSize())}
          style={{ width: "100%", height: "100%" }}
          center={[59.159161, 17.64566]}
          zoom={17}
          minZoom={4}
          maxZoom={18}
          preferCanvas
          zoomControl={false}
          wheelPxPerZoomLevel={120}
          zoomAnimation={false}
          maxBounds={[[-85, -180], [85, 180]]}
          maxBoundsViscosity={1.0}   // nice elastic clamp
          worldCopyJump={false}
        >
          <RemoveDefaultZoom />
          <ZoomControl position="bottomright" />
          <ChooseViewControl onClick={toggleViews} />

          <InstallBaseLayers urls={urls} onReady={(c) => { layersRef.current = c; }} />

          <MarkersLayer points={filtered} delayMode={delayMode} />
          <AutoFitBounds points={filtered} />
        </MapContainer>
      </div>
    </div>
  );
};

export default CommunicationHistoryMap;
